import type { CollectionItem } from "@/mocks/collections";

interface ProductInfoProps {
  product: CollectionItem;
}

export default function ProductInfo({ product }: ProductInfoProps) {
  const whatsappLink = `https://wa.me/923335281400?text=Hi%2C%20I%27m%20interested%20in%20this%20design.%20Can%20you%20share%20more%20details%3F`;

  return (
    <div className="flex flex-col">
      {/* Category */}
      <span className="font-display text-brand-gold text-[10px] tracking-[4px] uppercase mb-3">
        {product.category}
      </span>

      {/* Name */}
      <h1 className="font-serif text-3xl md:text-4xl text-brand-darkbrown font-bold mb-4 leading-tight">
        {product.name}
      </h1>

      {/* Showroom */}
      <p className="font-serif text-2xl md:text-3xl text-brand-brown font-bold mb-6">
        Visit Our Showroom
      </p>

      {/* Description */}
      <div className="mb-8">
        <p className="font-sans text-brand-muted text-sm leading-[1.9]">
          {product.description} — a timeless expression of artisanal mastery, hand-forged by sixth-generation
          craftsmen who carry forward techniques perfected since 1870. Every curve, every joint, every finish
          reflects an unbroken heritage of excellence passed from one master to the next, creating furniture that
          transcends mere function to become an heirloom of enduring beauty.
        </p>
      </div>

      {/* Divider */}
      <div className="h-px w-full bg-brand-gold/20 mb-8" />

      {/* WhatsApp CTA */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="whitespace-nowrap inline-flex items-center justify-center gap-3 font-display text-sm tracking-widest uppercase px-10 py-4 bg-transparent border-[1.5px] border-brand-gold text-brand-gold rounded-full hover:bg-brand-gold hover:text-brand-darkbrown transition-all duration-300 cursor-pointer mb-6 w-full md:w-auto"
      >
        <i className="ri-whatsapp-line text-lg" />
        Inquire on WhatsApp
      </a>

      {/* Secondary info */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-beige border border-brand-gold/20 shrink-0">
            <i className="ri-hammer-line text-brand-gold text-sm" />
          </div>
          <div>
            <p className="font-display text-[10px] tracking-[2px] uppercase text-brand-brown mb-0.5">Craft</p>
            <p className="font-sans text-xs text-brand-muted">Hand-carved solid wood</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-beige border border-brand-gold/20 shrink-0">
            <i className="ri-award-line text-brand-gold text-sm" />
          </div>
          <div>
            <p className="font-display text-[10px] tracking-[2px] uppercase text-brand-brown mb-0.5">Heritage</p>
            <p className="font-sans text-xs text-brand-muted">Est. 1870, Six Generations</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-beige border border-brand-gold/20 shrink-0">
            <i className="ri-tools-line text-brand-gold text-sm" />
          </div>
          <div>
            <p className="font-display text-[10px] tracking-[2px] uppercase text-brand-brown mb-0.5">Bespoke</p>
            <p className="font-sans text-xs text-brand-muted">Dimensions, wood &amp; finish</p>
          </div>
        </div>
      </div>
    </div>
  );
}